import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { CHATBOT_CONFIG } from "../../services/chatbotConfig";
import { Chat, chatbotService, Message } from "../../services";
import Cookies from "js-cookie";
import { decryptStringFromBytes, getUserIdFromJwt } from "../../utils";
const ENCRYPTION_KEY = import.meta.env.VITE_ENCRYPTION_KEY;

const LOADING_MESSAGES = CHATBOT_CONFIG.LOADING_MESSAGES;

export interface UserData { 
    userId?: number; 
    firstName?: string;
    [key: string]: any; 
}

export interface CurrentUser { 
    id: number; 
    name: string; 
    profileUrl: string; 
}



// --- Main Hook ---
export function useChatViewModel() {
   const jwtToken = Cookies.get("husk.Jwttoken") || "";
    const userId: string | number | null = getUserIdFromJwt(jwtToken);
   const EncryptedUserFirstName = Cookies.get("husk.FirstName");
   const userFirstName = decryptStringFromBytes(EncryptedUserFirstName || "", ENCRYPTION_KEY, ENCRYPTION_KEY);
    
    // Wait for userData to be available before creating currentUser
    const currentUser: CurrentUser | any = useMemo(() => {
        if (!userId) {
            return null;
        }
        const user = {
            id: userId,
            name: userFirstName,
            profileUrl: "https://huskpowersystems.com/wp-content/themes/Husk.png"
        };
        return user;
    }, [userId, userFirstName]);

    const [chats, setChats] = useState<Chat[]>([]);
    const [selectedChatId, setSelectedChatId] = useState<number | null>(null);
    const [pendingNewChat, setPendingNewChat] = useState<boolean>(false);
    const [messages, setMessages] = useState<Message[]>([
        { sender: "bot", text: CHATBOT_CONFIG.DEFAULT_BOT_MESSAGES.WELCOME }
    ]);
    const [input, setInput] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingMsgIdx, setLoadingMsgIdx] = useState<number>(0);
    const [chatSearch, setChatSearch] = useState<string>("");
    const loadingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const messagesEndRef = useRef<HTMLDivElement | null>(null);
    
    // Enhanced typing detection
    const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const handleInputChange = useCallback((value: string) => {
        setInput(value);
        setIsTyping(true);
        
        // Clear existing timeout
        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
        }
        
        // Set new timeout to stop typing indicator
        typingTimeoutRef.current = setTimeout(() => {
            setIsTyping(false);
        }, 1000);
    }, []);

    // Initial loading state to prevent UI jumping
    const [initialLoading, setInitialLoading] = useState<boolean>(true);
    const [isUIStable, setIsUIStable] = useState<boolean>(false);
    const [loadingMessages, setLoadingMessages] = useState<boolean>(false);
    const hasInitiallyScrolled = useRef<boolean>(false);

    // Performance and Animation States
    const [messageAnimations, setMessageAnimations] = useState<Record<string, {
        isEntering: boolean;
        opacity: number;
        transform: string;
    }>>({});
    const [isTyping, setIsTyping] = useState<boolean>(false);
    const [sendingMessage, setSendingMessage] = useState<boolean>(false);
    
    // Lazy Loading States
    const [visibleMessages, setVisibleMessages] = useState<number>(20);
    const [visibleChats, setVisibleChats] = useState<number>(15);
    const observerRef = useRef<IntersectionObserver | null>(null);
    const loadMoreRef = useRef<HTMLDivElement | null>(null);
    const [hasMoreMessages, setHasMoreMessages] = useState<boolean>(false);
    
    // Performance Optimization
    const [imagesLoaded, setImagesLoaded] = useState<Set<string>>(new Set());
    
    // Message virtualization for very large chats
    const [messageRenderBatch, setMessageRenderBatch] = useState<number>(0);
    const renderBatchSize = 50;

    // Responsive sidebar
    const [sidebarOpen, setSidebarOpen] = useState(false);

    // Wait for currentUser to be available before making API calls
    useEffect(() => {
        if (!currentUser?.id) {
            setInitialLoading(true);
            setIsUIStable(false);
            return;
        }
        
        setInitialLoading(true);
        setIsUIStable(false);
        
        (async () => {
            try {
                // Add small delay for better perceived performance
                await new Promise(resolve => setTimeout(resolve, 100));
                
                const chatList = await chatbotService.fetchChats(currentUser.id);
                
                setChats(chatList || []);
                
                if (chatList && chatList.length > 0) {
                    // Load first chat with delay for smooth transition
                    setTimeout(() => {
                        setSelectedChatId(chatList[0].id);
                        setPendingNewChat(false);
                    }, 150);
                } else {
                    setPendingNewChat(true);
                    setSelectedChatId(null);
                }
                
                // Staggered loading completion for smooth UI
                setTimeout(() => {
                    setInitialLoading(false);
                    setTimeout(() => {
                        setIsUIStable(true);
                    }, 100);
                }, 200);
                
            } catch (error) {
                if ((error as any)?.name === 'AbortError') return; // Request was cancelled
                console.error('Error fetching chats:', error);
                
                setChats([]);
                setPendingNewChat(true);
                setSelectedChatId(null);
                
                setTimeout(() => {
                    setInitialLoading(false);
                    setTimeout(() => {
                        setIsUIStable(true);
                    }, 100);
                }, 200);
            }
        })();
        
        // Cleanup function
        return () => {
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }
        };
    }, [currentUser?.id]);

    const sortedChats = useMemo(() => {
        if (!selectedChatId) return chats;
        const curr = chats.find(c => c.id === selectedChatId);
        const rest = chats.filter(c => c.id !== selectedChatId);
        return curr ? [curr, ...rest.sort((a, b) => b.id - a.id)] : [...rest.sort((a, b) => b.id - a.id)];
    }, [chats, selectedChatId]);
    const filteredChats = sortedChats.filter(c =>
        (c.title || "").toLowerCase().includes(chatSearch.toLowerCase())
    );

    useEffect(() => {
        if (selectedChatId && !pendingNewChat) {
            setLoadingMessages(true);
            setIsUIStable(false);
            
            chatbotService.fetchMessages(selectedChatId)
                .then((msgs: Message[]) => {
                    // Count existing negative feedback for this chat
                    const existingNegativeFeedback = msgs.filter(m => m.feedback === 0).length;
                    if (existingNegativeFeedback > 0) {
                        setNegativeFeedbackCount(prev => ({
                            ...prev,
                            [selectedChatId]: existingNegativeFeedback
                        }));
                    }

                    const newMessages = msgs && msgs.length > 0
                        ? msgs.map((m: Message, index: number) => {
                            const enrichedMessage = {
                                sender: m.sender,
                                text: m.text,
                                exchange_id: m.exchange_id,
                                id: m.id, // Keep the actual database ID
                                uiId: `ui_${selectedChatId}_${index}_${Date.now()}`, // Separate UI ID for animations
                                feedback: m.feedback // Preserve feedback from API
                            };
                            
                            return enrichedMessage;
                        })
                        : [{ 
                            sender: "bot", 
                            text: CHATBOT_CONFIG.DEFAULT_BOT_MESSAGES.WELCOME_HMG,
                            uiId: `welcome_${Date.now()}`
                        }];
                    
                    setMessages(newMessages);
                    setHasMoreMessages(newMessages.length > 20);
                    
                    // Trigger enter animations for messages
                    setTimeout(() => {
                        newMessages.forEach((msg, index) => {
                            if (msg.uiId) {
                                setTimeout(() => {
                                    triggerMessageAnimation(msg.uiId!, msg.sender === 'user');
                                }, index * 50); // Faster stagger for better performance
                            }
                        });
                    }, 100);
                    
                    // Smooth loading completion
                    setTimeout(() => {
                        setLoadingMessages(false);
                        setTimeout(() => {
                            setIsUIStable(true);
                        }, 100);
                    }, 300);
                })
                .catch((error) => {
                    // Handle different types of cancellation errors
                    if ((error as any)?.name === 'AbortError' || 
                        error.message?.includes('Request was cancelled') ||
                        error.message?.includes('aborted')) {
                        // For cancelled requests, try to load cached data or show welcome message
                        setMessages([{ 
                            sender: "bot", 
                            text: CHATBOT_CONFIG.DEFAULT_BOT_MESSAGES.WELCOME_HMG,
                            uiId: `welcome_${Date.now()}`
                        }]);
                        setLoadingMessages(false);
                        setIsUIStable(true);
                        return;
                    }
                    
                    console.error("Error fetching messages:", error);
                    setMessages([{ 
                        sender: "bot", 
                        text: CHATBOT_CONFIG.DEFAULT_BOT_MESSAGES.NO_MESSAGES,
                        uiId: `error_${Date.now()}`
                    }]);
                    setTimeout(() => {
                        setLoadingMessages(false);
                        setTimeout(() => {
                            setIsUIStable(true);
                        }, 100);
                    }, 300);
                });
        } else if (pendingNewChat) {
            const welcomeMessage = { 
                sender: "bot", 
                text: CHATBOT_CONFIG.DEFAULT_BOT_MESSAGES.WELCOME_HMG,
                uiId: `welcome_new_${Date.now()}`
            };
            setMessages([welcomeMessage]);
            setLoadingMessages(false);
            setIsUIStable(true);
            
            // Animate welcome message
            setTimeout(() => {
                triggerMessageAnimation(welcomeMessage.uiId!, false);
            }, 200);
        }
    }, [selectedChatId, pendingNewChat]);

    useEffect(() => {
        // Only auto-scroll if UI is stable, not loading messages, and not during initial loading
        if (isUIStable && !initialLoading && !loadingMessages) {
            // For initial load, scroll immediately without animation
            if (!hasInitiallyScrolled.current) {
                setTimeout(() => {
                    messagesEndRef.current?.scrollIntoView({ behavior: "instant" });
                    hasInitiallyScrolled.current = true;
                }, 50);
            } else {
                // For subsequent message updates, use smooth scrolling
                setTimeout(() => {
                    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
                }, 100);
            }
        }
    }, [messages, loading, isUIStable, initialLoading, loadingMessages]);

    useEffect(() => {
        if (loading) {
            loadingIntervalRef.current = setInterval(() => {
                setLoadingMsgIdx(idx => (idx + 1) % LOADING_MESSAGES.length);
            }, CHATBOT_CONFIG.LOADING_INTERVAL);
        } else {
            if (loadingIntervalRef.current) clearInterval(loadingIntervalRef.current);
            setLoadingMsgIdx(0);
        }
        return () => {
            if (loadingIntervalRef.current) clearInterval(loadingIntervalRef.current);
            if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        };
    }, [loading]);

    function getPreviousExchangeId(): string | undefined {
        for (let i = messages.length - 1; i >= 0; i--) {
            if (messages[i].sender === "bot" && messages[i].exchange_id) {
                return messages[i].exchange_id;
            }
        }
        return undefined;
    }

    // Build conversation history for AI context - enables "different format", "change to chart" etc.
    function buildConversationHistory(): Array<{role: string, content: string}> {
        const history: Array<{role: string, content: string}> = [];
        
        messages.forEach(msg => {
            // Skip loading messages and empty messages
            if (msg.type !== "loading" && msg.text.trim()) {
                const role = msg.sender === "user" ? "user" : "assistant";
                history.push({ role, content: msg.text });
            }
        });
        
        // Limit to last 20 messages (10 exchanges) to avoid token limits
        return history.slice(-20);
    }

    const handleSend = async () => {
        if (!input.trim() || loading || !currentUser?.id) return;

        const currentInputText = input;
        setInput("");
        setLoading(true);

        // Reset current response time when starting new request
        setCurrentResponseTime(null);

        setSendingMessage(true);
        setMessages((msgs) => [
            ...msgs,
            { 
                sender: "user", 
                text: currentInputText,
                uiId: `user_${Date.now()}_${Math.random()}`
            },
            { 
                sender: "bot", 
                text: "", 
                type: "loading",
                uiId: `loading_${Date.now()}`
            }
        ]);

        // Smooth animation delay
        await new Promise(res => setTimeout(res, 100));

        try {
            let chatId = selectedChatId;
            let newChatCreated = false;

            if (pendingNewChat || !selectedChatId) {
                const newChat = await chatbotService.createChat({ userId: currentUser.id, title: currentInputText.trim() });
                chatId = newChat.id;
                newChatCreated = true;
            }

            // Add user message and get the database ID
            const savedUserMessage = await chatbotService.addMessage({ 
                chatId: chatId!, 
                sender: "user", 
                text: currentInputText 
            });

            let previousExchangeId: string | undefined = undefined;
            if (!pendingNewChat && selectedChatId) {
                previousExchangeId = getPreviousExchangeId();
            }

            // Build conversation history for better context
            const conversationHistory = buildConversationHistory();

            let apiResponse;
            let responseTimeMs = 0; // Initialize response time
            const apiStartTime = performance.now(); // Start measuring time
            
            // Try with conversation history first for better context
            if (conversationHistory.length > 0) {
                try {
                    apiResponse = await chatbotService.sendMessageToAPIWithHistory({ 
                        message: currentInputText, 
                        conversationHistory,
                        exchange_id: previousExchangeId 
                    });
                    
                    const apiEndTime = performance.now();
                    responseTimeMs = Math.round(apiEndTime - apiStartTime);
                    
                } catch (error) {
                    // Reset timer for fallback API call
                    const fallbackStartTime = performance.now();
                    
                    // Fallback to simple API if history fails
                    apiResponse = await chatbotService.sendMessageToAPI({ 
                        message: currentInputText, 
                        exchange_id: previousExchangeId 
                    });
                    
                    const fallbackEndTime = performance.now();
                    responseTimeMs = Math.round(fallbackEndTime - fallbackStartTime);
                }
            } else {
                // For first message, use simple API
                apiResponse = await chatbotService.sendMessageToAPI({ 
                    message: currentInputText, 
                    exchange_id: previousExchangeId 
                });
                
                const apiEndTime = performance.now();
                responseTimeMs = Math.round(apiEndTime - apiStartTime);
            }

            // Update response time states
            setCurrentResponseTime(responseTimeMs);
            setLastResponseTime(responseTimeMs);

            // Add bot message and get the database ID, including our calculated response time
            const savedBotMessage = await chatbotService.addMessage({
                chatId: chatId!,
                sender: "bot",
                text: apiResponse.response || apiResponse.reply || JSON.stringify(apiResponse),
                exchange_id: apiResponse.exchange_id,
                ai_agent_response_time: responseTimeMs // Store our calculated response time in database
            });

            setMessages(msgs => {
                const loadingIdx = msgs.map((m: any, i) => [m, i]).reverse().find(([m]) => m.type === "loading")?.[1];
                const userIdx = msgs.map((m: any, i) => [m, i]).reverse().find(([m]) => m.sender === "user" && m.uiId)?.[1];
                
                if (loadingIdx === undefined || userIdx === undefined) return msgs;
                
                const newMsgs = [...msgs];
                const botMessageUiId = `bot_${Date.now()}_${Math.random()}`;
                const userMessageUiId = `user_${Date.now()}_${Math.random()}`;
                
                // Update user message with database ID
                newMsgs[userIdx] = {
                    ...savedUserMessage,
                    uiId: userMessageUiId
                };
                
                // Replace loading message with bot response including database ID
                newMsgs[loadingIdx] = {
                    ...savedBotMessage,
                    uiId: botMessageUiId
                };
                
                // Trigger animation for bot response
                setTimeout(() => {
                    triggerMessageAnimation(botMessageUiId, false);
                }, 100);
                
                return newMsgs;
            });

            if (newChatCreated) {
                const chatList = await chatbotService.fetchChats(currentUser.id);
                setChats(chatList || []);
                setSelectedChatId(chatId);
                setPendingNewChat(false);
            }

        } catch (e) {
            console.error('Error sending message:', e);
            setMessages(msgs => {
                const idx = msgs.map((m: any, i) => [m, i]).reverse().find(([m]) => m.type === "loading")?.[1];
                if (idx === undefined) return msgs;
                const newMsgs = [...msgs];
                newMsgs[idx] = { 
                    sender: "bot", 
                    text: CHATBOT_CONFIG.DEFAULT_BOT_MESSAGES.ERROR,
                    uiId: `error_${Date.now()}`
                };
                return newMsgs;
            });
        }
        setLoading(false);
        setSendingMessage(false);
    };

    const handleRefresh = () => {
        // Reset scroll state
        hasInitiallyScrolled.current = false;
        setIsUIStable(false);
        setLoadingMessages(false);
        
        setPendingNewChat(true);
        setSelectedChatId(null);
        setMessages([
            {
                sender: "bot",
                text: CHATBOT_CONFIG.DEFAULT_BOT_MESSAGES.WELCOME
            }
        ]);
        setInput("");
        
        // Re-enable stable UI immediately for new chat
        setTimeout(() => {
            setIsUIStable(true);
        }, 100);
    };

    const handleSelectChat = (chatId: number) => {
        // Reset scroll state when selecting new chat
        hasInitiallyScrolled.current = false;
        setIsUIStable(false);
        setLoadingMessages(true);
        
        setSelectedChatId(chatId);
        setPendingNewChat(false);
        setInput("");
        setChats(prev => {
            const idx = prev.findIndex(c => c.id === chatId);
            if (idx <= 0) return prev;
            const chat = prev[idx];
            const rest = prev.filter((c, i) => i !== idx);
            return [chat, ...rest];
        });
        setSidebarOpen(false); // close sidebar on mobile
    };

    // Animation helper functions
    const triggerMessageAnimation = useCallback((messageKey: string, isUser: boolean = false) => {
        // Set initial animation state
        setMessageAnimations(prev => ({
            ...prev,
            [messageKey]: {
                isEntering: true,
                opacity: 0,
                transform: isUser ? 'translateX(30px) scale(0.95)' : 'translateX(-30px) scale(0.95)'
            }
        }));
        
        // Animate to final state after a short delay
        setTimeout(() => {
            setMessageAnimations(prev => ({
                ...prev,
                [messageKey]: {
                    isEntering: false,
                    opacity: 1,
                    transform: 'translateX(0) scale(1)'
                }
            }));
        }, 50);
        
        // Clean up animation state after animation completes
        setTimeout(() => {
            setMessageAnimations(prev => {
                const newState = { ...prev };
                delete newState[messageKey];
                return newState;
            });
        }, 500);
    }, []);

    // Lazy image loading for better performance
    const lazyLoadImage = useCallback((src: string) => {
        if (imagesLoaded.has(src)) return;
        
        const img = new Image();
        img.onload = () => {
            setImagesLoaded(prev => new Set([...prev, src]));
        };
        img.src = src;
    }, [imagesLoaded]);

    // Preload critical images
    useEffect(() => {
        if (currentUser?.profileUrl) {
            lazyLoadImage(currentUser.profileUrl);
        }
        lazyLoadImage('https://www.w3schools.com/howto/img_avatar.png');
    }, [currentUser?.profileUrl, lazyLoadImage]);

    // Lazy loading for messages with Intersection Observer
    useEffect(() => {
        if (messages.length <= visibleMessages || !hasMoreMessages) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && visibleMessages < messages.length) {
                    setVisibleMessages(prev => Math.min(prev + 15, messages.length));
                }
            },
            { threshold: 0.1, rootMargin: '100px' }
        );

        if (loadMoreRef.current) {
            observer.observe(loadMoreRef.current);
        }

        observerRef.current = observer;

        return () => {
            observer.disconnect();
        };
    }, [messages.length, visibleMessages, hasMoreMessages]);

    // Reset visible messages when switching chats
    useEffect(() => {
        setVisibleMessages(20);
        setHasMoreMessages(false);
        setMessageRenderBatch(0);
    }, [selectedChatId]);

    // Get visible messages for rendering with performance optimization
    const visibleMessageList = useMemo(() => {
        const startIdx = Math.max(0, messages.length - visibleMessages);
        return messages.slice(startIdx);
    }, [messages, visibleMessages]);
    
    // Performance helper for very large chat histories
    const getOptimizedMessages = useMemo(() => {
        if (visibleMessageList.length <= renderBatchSize) {
            return visibleMessageList;
        }
        
        // For very large chats, render in batches to prevent UI lag
        const totalBatches = Math.ceil(visibleMessageList.length / renderBatchSize);
        const currentBatch = Math.min(messageRenderBatch, totalBatches - 1);
        const startIdx = Math.max(0, visibleMessageList.length - (currentBatch + 1) * renderBatchSize);
        
        return visibleMessageList.slice(startIdx);
    }, [visibleMessageList, messageRenderBatch, renderBatchSize]);

    // Feedback functionality
    const [feedbackLoading, setFeedbackLoading] = useState<Record<string, boolean>>({});
    const [negativeFeedbackCount, setNegativeFeedbackCount] = useState<Record<number, number>>({});
    const [showFeedbackPopup, setShowFeedbackPopup] = useState<boolean>(false);
    const [feedbackPopupMessageId, setFeedbackPopupMessageId] = useState<number | null>(null);
    const [detailedFeedback, setDetailedFeedback] = useState<string>('');
    const [sendingDetailedFeedback, setSendingDetailedFeedback] = useState<boolean>(false);
    
    const handleMessageFeedback = useCallback(async (messageId: string | number, feedbackFlag: number) => {
        try {
            if (!messageId || !selectedChatId) {
                console.error('No message ID or chat ID provided for feedback');
                return;
            }

            // Validate that we have a proper database ID (not a UI ID)
            if (typeof messageId === 'string' && (messageId.startsWith('ui_') || messageId.startsWith('welcome_') || messageId.startsWith('error_'))) {
                console.error('Cannot send feedback for UI-only message:', messageId);
                return;
            }

            // Set loading state for this specific message using the messageId as key
            setFeedbackLoading(prev => ({ ...prev, [messageId]: true }));

            // Immediately update UI for better user experience
            setMessages(prevMessages => 
                prevMessages.map(msg => 
                    msg.id === messageId 
                        ? { ...msg, feedback: feedbackFlag }
                        : msg
                )
            );
            
            // Send feedback to API - ensure messageId is a number
            const numericMessageId = typeof messageId === 'string' ? parseInt(messageId) : messageId;
            if (isNaN(numericMessageId)) {
                throw new Error('Invalid message ID for feedback');
            }
            
            await chatbotService.sendMessageFeedback({ 
                messageId: numericMessageId, 
                feedbackFlag 
            });

            // Track negative feedback count per chat
            if (feedbackFlag === 0) {
                setNegativeFeedbackCount(prev => {
                    const currentCount = prev[selectedChatId] || 0;
                    const newCount = currentCount + 1;
                    
                    // Show popup if this is the 3rd negative feedback (and no more than 3)
                    if (newCount === 3) {
                        setFeedbackPopupMessageId(numericMessageId);
                        setShowFeedbackPopup(true);
                    }
                    
                    return {
                        ...prev,
                        [selectedChatId]: newCount
                    };
                });
            }
            
        } catch (error) {
            console.error('Error sending feedback:', error);
            
            // Revert the UI change if API call failed
            setMessages(prevMessages => 
                prevMessages.map(msg => 
                    msg.id === messageId 
                        ? { ...msg, feedback: null }
                        : msg
                )
            );
            
            // Show error toast
            console.error('Failed to send feedback. Please try again.');
        } finally {
            // Remove loading state
            setFeedbackLoading(prev => {
                const newState = { ...prev };
                delete newState[messageId];
                return newState;
            });
        }
    }, [selectedChatId]);

    // Handle detailed feedback submission
    const handleDetailedFeedback = useCallback(async () => {
        if (!feedbackPopupMessageId || !detailedFeedback.trim()) {
            console.error('No message ID or feedback message provided');
            return;
        }

        try {
            setSendingDetailedFeedback(true);
            
            await chatbotService.sendDetailedFeedback({
                messageId: feedbackPopupMessageId,
                feedbackMessage: detailedFeedback.trim()
            });
            
            // Close popup and reset state
            setShowFeedbackPopup(false);
            setFeedbackPopupMessageId(null);
            setDetailedFeedback('');
            
        } catch (error) {
            console.error('Error sending detailed feedback:', error);
            // Keep popup open on error
        } finally {
            setSendingDetailedFeedback(false);
        }
    }, [feedbackPopupMessageId, detailedFeedback]);

    // Handle closing the feedback popup
    const handleCloseFeedbackPopup = useCallback(() => {
        setShowFeedbackPopup(false);
        setFeedbackPopupMessageId(null);
        setDetailedFeedback('');
    }, []);

    // Reset negative feedback count when switching chats
    useEffect(() => {
        // Don't reset count, keep it persistent per chat
        // setNegativeFeedbackCount({});
    }, [selectedChatId]);

    // Response time tracking
    const [currentResponseTime, setCurrentResponseTime] = useState<number | null>(null);
    const [lastResponseTime, setLastResponseTime] = useState<number | null>(null);
    const [responseTimeStats, setResponseTimeStats] = useState<{
        averageResponseTime: number;
        minResponseTime: number;
        maxResponseTime: number;
        totalBotMessages: number;
        messagesWithTiming: number;
    } | null>(null);

    // Update response time stats when messages change
    useEffect(() => {
        if (selectedChatId && messages.length > 0) {
            chatbotService.getResponseTimeStats(selectedChatId)
                .then(stats => {
                    setResponseTimeStats(stats);
                })
                .catch(error => {
                    console.error('Error getting response time stats:', error);
                });
        }
    }, [selectedChatId, messages.length]);

    // Utility function to format response time
    const formatResponseTime = useCallback((timeMs: number | null): string => {
        if (timeMs === null) return 'N/A';
        
        if (timeMs < 1000) {
            return `${timeMs}ms`;
        } else {
            return `${(timeMs / 1000).toFixed(2)}s`;
        }
    }, []);

    // Calculate question count for current chat
    const currentChatQuestionCount = useMemo(() => {
        if (!selectedChatId) return 0;
        return messages.filter(msg => msg.sender?.toLowerCase() === "user").length;
    }, [messages, selectedChatId]);

    const returnState = {
        chats, 
        selectedChatId, 
        pendingNewChat, 
        messages, // Keep all messages for count calculation
        visibleMessageList, // Add visible messages for chunked rendering
        input, 
        loading, 
        loadingMsgIdx,
        chatSearch, 
        setChatSearch, 
        setInput: handleInputChange, // Use enhanced input handler with typing detection
        handleSend, 
        handleRefresh, 
        handleSelectChat,
        currentUser: currentUser || { 
            id: 0, 
            name: "Loading...", 
            profileUrl: "https://huskpowersystems.com/wp-content/themes/Husk.png" 
        }, 
        filteredChats: filteredChats.slice(0, visibleChats), // Lazy load chats too
        messagesEndRef,
        sidebarOpen, 
        setSidebarOpen, 
        // Only show initial loading if we don't have user data OR if we're actually loading
        initialLoading: !currentUser ? true : initialLoading, 
        // Only stable if user data is available AND UI is stable
        isUIStable: currentUser ? isUIStable : false, 
        loadingMessages,
        // Animation and performance states
        messageAnimations,
        isTyping,
        sendingMessage,
        hasMoreMessages,
        // Chunked loading properties
        visibleMessages,
        totalMessages: messages.length,
        // Helper for UI to show load more indicator
        loadMoreRef,
        // Image loading state
        imagesLoaded: imagesLoaded.has.bind(imagesLoaded),
        lazyLoadImage,
        // Feedback functionality
        handleMessageFeedback,
        feedbackLoading,
        // Detailed feedback popup
        showFeedbackPopup,
        detailedFeedback,
        setDetailedFeedback,
        handleDetailedFeedback,
        handleCloseFeedbackPopup,
        sendingDetailedFeedback,
        negativeFeedbackCount: negativeFeedbackCount[selectedChatId || 0] || 0,
        responseTimeStats,
        // Real-time response time tracking
        currentResponseTime,
        lastResponseTime,
        formatResponseTime,
        // Question count for current chat
        currentChatQuestionCount
    };

    return returnState;
}